import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Pool,
  Player,
  Team,
  PlayerPick,
  Position,
  getPositionConfig,
  CFP_POSITIONS,
  getCountryFlag,
  isRosterSelectionPicks
} from '../types/pool';
import { api } from '../services/api';
import FeeNotice from '../components/FeeNotice';

// Position order for display
const POSITION_DISPLAY_ORDER: Position[] = [
  // Football positions (CFP/NFL)
  'QB',
  'RB',
  'WR',
  'TE',
  'FLEX',
  'K',
  'DEF',
  // NHL positions
  'C',
  'LW',
  'RW',
  // NBA positions (generic: G, F, C)
  'F',
  // Shared positions (Hockey/Basketball)
  'F1', 'F2', 'F3', 'F4', 'F5',
  'D1', 'D2', 'D3', 'D4',
  'D',
  'G',
  'G1', 'G2',
  'C1', 'C2',
  'FLEX1', 'FLEX2',
] as Position[];

// Sort positions in the correct display order
function sortPositionKeys(positions: any): Position[] {
  const keys = Object.keys(positions) as Position[];
  return keys.sort((a, b) => {
    const indexA = POSITION_DISPLAY_ORDER.indexOf(a);
    const indexB = POSITION_DISPLAY_ORDER.indexOf(b);
    
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    return 0;
  });
}

// Group Olympic positions by type
const OLYMPIC_POSITION_GROUPS = {
  'F': { label: 'Forwards', slots: ['F1', 'F2', 'F3', 'F4', 'F5'], count: 5, position: 'F' },
  'D': { label: 'Defensemen', slots: ['D1', 'D2', 'D3', 'D4'], count: 4, position: 'D' },
  'G': { label: 'Goaltender', slots: ['G'], count: 1, position: 'G' },
};

export function EntryForm() {
  const { poolId, entryId } = useParams<{ poolId: string; entryId?: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const [pool, setPool] = useState<Pool | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [entryName, setEntryName] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState<Record<string, PlayerPick>>({});
  const [expandedPosition, setExpandedPosition] = useState<Position | keyof typeof OLYMPIC_POSITION_GROUPS | null>(null);

  const isEditMode = !!entryId;
  const hasEntryFee = pool?.entryFee && Number(pool.entryFee) > 0;

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
      return;
    }
    if (poolId) {
      loadData();
    }
  }, [poolId, isAuthenticated]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [poolData, playersData, teamsData] = await Promise.all([
        api.getPool(poolId!),
        api.getPoolPlayers(poolId!),
        api.getPoolTeams(poolId!),
      ]);
      setPool(poolData);
      setPlayers(playersData.players || []);
      setTeams(teamsData);

      // If editing, load existing entry by finding it among all user entries
      if (isEditMode && entryId) {
        const token = await getAccessTokenSilently();
        const myEntries = await api.getMyEntries(poolId!, token);
        const myEntry = myEntries.find(e => e.id === entryId);

        if (!myEntry) {
          setError('Entry not found');
          return;
        }

        // Check if pool is still open
        if (poolData.status !== 'OPEN') {
          setError('Cannot edit entry after deadline has passed');
          return;
        }

        // Load existing picks
        setEntryName(myEntry.entryName || '');
        if (isRosterSelectionPicks(myEntry.picks) && myEntry.picks.positions) {
          setSelectedPlayers(myEntry.picks.positions);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pool data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get teams used in roster
  const getUsedTeams = (): Set<string> => {
    return new Set(Object.values(selectedPlayers).map(p => p.teamId));
  };

  // Check if a team is already used
  const isTeamUsed = (teamId: string): boolean => {
    return getUsedTeams().has(teamId);
  };

  // Get position config for this pool type
  const getPositions = () => {
    if (!pool) return CFP_POSITIONS as any; // Fallback during load
    return getPositionConfig(pool.type);
  };

  // Check if position is filled
  const isPositionFilled = (position: Position): boolean => {
    const slots = getPositionSlots(position);
    return slots.every(slot => selectedPlayers[slot] !== undefined);
  };

  // Get slots for a position
  const getPositionSlots = (position: Position): string[] => {
    const positions = getPositions();
    const count = positions[position as keyof typeof positions]?.count || 1;
    return Array.from({ length: count }, (_, i) => 
      count > 1 ? `${position}${i + 1}` : position
    );
  };

  // Select a player for a position slot
  const selectPlayer = (slot: string, player: Player) => {
    setSelectedPlayers(prev => ({
      ...prev,
      [slot]: {
        playerId: player.id,
        playerName: player.name,
        teamId: player.teamId,
        position: player.position,
      },
    }));
  };

  // Remove a player from a slot
  const removePlayer = (slot: string) => {
    setSelectedPlayers(prev => {
      const newPicks = { ...prev };
      delete newPicks[slot];
      return newPicks;
    });
  };

  // Filter players for a position
  const getAvailablePlayersForPosition = (position: Position): Player[] => {
    const positions = getPositions();
    const eligible = positions[position as keyof typeof positions]?.eligible || [];
    
    return players.filter(player => {
      // Check position match based on eligible positions
      const positionMatch = eligible.includes(player.position);
      
      if (!positionMatch) return false;

      // Check if team is already used (unless this player is already selected)
      const isAlreadySelected = Object.values(selectedPlayers).some(
        p => p.playerId === player.id
      );
      
      if (isAlreadySelected) return true;
      
      return !isTeamUsed(player.teamId);
    });
  };

  // Validate roster is complete
  const isRosterComplete = (): boolean => {
    const positions = getPositions();
    const requiredSlots = Object.keys(positions).flatMap(pos => 
      getPositionSlots(pos as Position)
    );
    return requiredSlots.every(slot => selectedPlayers[slot] !== undefined);
  };

  // Submit entry
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!entryName.trim()) {
      alert('Please enter a name for your entry.');
      return;
    }
    
    if (!isRosterComplete()) {
      alert('Please fill all roster positions before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      const token = await getAccessTokenSilently();

      const entryData: any = {
        entryName: entryName.trim(),
        picks: {
          type: 'ROSTER_SELECTION' as const,
          positions: selectedPlayers,
        },
      };

      if (isEditMode && entryId) {
        // Update existing entry
        await api.updateEntry(poolId!, entryId, entryData, token);
        alert('Entry updated successfully!');
        navigate(`/pools/${poolId}`);
      } else {
        // Create new entry
        await api.createEntry(poolId!, entryData, token);
        if (hasEntryFee) {
          // Payment v2: entry is saved — take them to Settle Up to pay
          navigate('/settle-up', {
            state: { justSubmitted: true, submittedPool: pool?.name },
          });
        } else {
          alert('Entry submitted successfully!');
          navigate(`/pools/${poolId}`);
        }
      }
    } catch (err: any) {
      if (err?.message?.includes('login_required') || err?.message?.includes('Missing Refresh Token') || err?.error === 'login_required') {
        alert('Your session has expired. Please log in again.');
      } else {
        alert(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'submit'} entry`);
      }
      console.error(`Error ${isEditMode ? 'updating' : 'submitting'} entry:`, err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pool data...</p>
        </div>
      </div>
    );
  }

  if (error || !pool) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md">
          <div className="text-red-600 text-center">
            <h3 className="text-lg font-semibold mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error || 'Pool not found'}</p>
            <Link
              to={`/pools/${poolId}`}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Back to Pool
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const positions = getPositions();
  const usedTeams = getUsedTeams();
  const usedCount = usedTeams.size;
  const totalSlots = Object.keys(selectedPlayers).length;
  const requiredSlots: number = Object.values(positions).reduce((sum: number, p: any) => sum + (p?.count || 0), 0);
  const isOlympic = pool.type === 'OLYMPIC_HOCKEY';

  // Olympic-specific helper: Get available players for a position group
  const getOlympicAvailablePlayersByGroup = (positionGroup: keyof typeof OLYMPIC_POSITION_GROUPS) => {
    const group = OLYMPIC_POSITION_GROUPS[positionGroup];
    return players.filter(player => {
      if (player.position !== group.position) return false;
      const isAlreadySelected = Object.values(selectedPlayers).some(p => p.playerId === player.id);
      if (isAlreadySelected) return true;
      return !isTeamUsed(player.teamId);
    });
  };

  // Olympic-specific helper: Select player for next available slot in group
  const selectOlympicPlayer = (positionGroup: keyof typeof OLYMPIC_POSITION_GROUPS, player: Player) => {
    const group = OLYMPIC_POSITION_GROUPS[positionGroup];
    const emptySlot = group.slots.find(slot => !selectedPlayers[slot]);
    if (emptySlot) {
      selectPlayer(emptySlot, player);
    }
  };

  // Olympic-specific helper: Get unused countries with flags
  const getUnusedCountries = () => {
    return teams.filter(team => !usedTeams.has(team.id)).map(team => ({
      ...team,
      flag: getCountryFlag(team.name)
    }));
  };

  // Render Olympic Hockey grouped positions
  const renderOlympicPositions = () => {
    return Object.entries(OLYMPIC_POSITION_GROUPS).map(([key, group]) => {
      const positionGroup = key as keyof typeof OLYMPIC_POSITION_GROUPS;
      const selectedInGroup = group.slots.filter(slot => selectedPlayers[slot]);
      const isFilled = selectedInGroup.length === group.count;
      const isExpanded = expandedPosition === positionGroup;
      const availablePlayers = getOlympicAvailablePlayersByGroup(positionGroup);

      return (
        <div key={positionGroup} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Group Header */}
          <button
            type="button"
            onClick={() => setExpandedPosition(isExpanded ? null : positionGroup)}
            className="w-full px-6 py-4 bg-gradient-to-r from-blue-50 to-white hover:from-blue-100 hover:to-blue-50 transition-all border-b border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-lg">📋</span>
                <span className="text-base font-bold text-gray-900">
                  {group.label}
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  ({selectedInGroup.length}/{group.count} selected)
                </span>
              </div>
              <div className="flex items-center gap-3">
                {isFilled && (
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete
                  </span>
                )}
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </button>

          {/* Group Content */}
          {isExpanded && (
            <div className="p-6 bg-gray-50">
              {/* Instructions */}
              <p className="text-sm text-gray-600 mb-4 italic">
                Click players below to add to the next available slot
              </p>

              {/* Selected Players */}
              <div className="mb-6 space-y-2">
                {group.slots.map((slot) => {
                  const selectedPlayer = selectedPlayers[slot];
                  if (!selectedPlayer) {
                    return (
                      <div key={slot} className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center bg-white">
                        <span className="text-sm font-medium text-gray-400">
                          Empty slot
                        </span>
                      </div>
                    );
                  }

                  const playerData = players.find(p => p.id === selectedPlayer.playerId);
                  const countryFlag = playerData ? getCountryFlag(playerData.team.name) : '';

                  return (
                    <div key={slot} className="flex items-center justify-between p-4 bg-white border-2 border-emerald-400 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 text-sm">
                          {countryFlag} {selectedPlayer.playerName}
                        </p>
                        <p className="text-xs text-gray-600">{playerData?.team.name}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePlayer(slot)}
                        className="ml-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                        title="Remove player"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Available Players */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Available Players</h4>
                {availablePlayers.length === 0 ? (
                  <p className="text-sm text-gray-500 italic p-4 bg-white rounded-lg text-center border border-gray-200">
                    No available players (all countries used)
                  </p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1.5 pr-2">
                    {availablePlayers.map((player) => {
                      const isAlreadySelected = Object.values(selectedPlayers).some(
                        p => p.playerId === player.id
                      );
                      const isCountryBlocked = isTeamUsed(player.teamId) && !isAlreadySelected;
                      const countryFlag = getCountryFlag(player.team.name);

                      return (
                        <button
                          key={player.id}
                          type="button"
                          onClick={() => selectOlympicPlayer(positionGroup, player)}
                          disabled={isAlreadySelected || isCountryBlocked}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                            isCountryBlocked
                              ? 'bg-red-50 border-2 border-red-200 text-gray-400 cursor-not-allowed opacity-70'
                              : isAlreadySelected
                              ? 'bg-emerald-50 border-2 border-emerald-400 text-gray-700 cursor-default'
                              : 'bg-white border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-gray-900 shadow-sm hover:shadow'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <p className={`font-semibold text-sm ${
                                isCountryBlocked ? 'text-gray-400' : 'text-gray-900'
                              }`}>
                                {countryFlag} {player.name}
                              </p>
                              <p className={`text-xs mt-0.5 ${
                                isCountryBlocked ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {player.team.name}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-semibold px-2 py-1 rounded ${
                                isCountryBlocked 
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {player.position}
                              </span>
                              {isCountryBlocked && (
                                <span className="text-sm font-bold text-red-500" title="Country already used">
                                  ✕
                                </span>
                              )}
                              {isAlreadySelected && (
                                <span className="text-sm font-bold text-emerald-500" title="Already selected">
                                  ✓
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  // Render standard positions (CFP, NFL, etc.)
  const renderStandardPositions = () => {
    return sortPositionKeys(positions).map((position) => {
      const positionConfig = positions[position as keyof typeof positions];
      const slots = getPositionSlots(position);
      const isFilled = isPositionFilled(position);
      const isExpanded = expandedPosition === position;
      const availablePlayers = getAvailablePlayersForPosition(position);

      return (
        <div key={position} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Position Header - Clickable */}
          <button
            type="button"
            onClick={() => setExpandedPosition(isExpanded ? null : position)}
            className="w-full px-6 py-4 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-all border-b border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-base font-bold text-gray-900">
                  {positionConfig.label}
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  {positionConfig.count} {positionConfig.count > 1 ? 'slots' : 'slot'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {isFilled && (
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete
                  </span>
                )}
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </button>

          {/* Position Content - Collapsible */}
          {isExpanded && (
            <div className="p-6 bg-gray-50">
              {/* Selected Players */}
              <div className="mb-6 space-y-2">
                {slots.map((slot) => {
                  const selectedPlayer = selectedPlayers[slot];
                  if (!selectedPlayer) {
                    return (
                      <div key={slot} className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center bg-white">
                        <span className="text-sm font-medium text-gray-400">
                          Empty slot {slots.length > 1 ? `#${slot.slice(-1)}` : ''}
                        </span>
                      </div>
                    );
                  }

                  const playerData = players.find(p => p.id === selectedPlayer.playerId);
                  return (
                    <div key={slot} className="flex items-center justify-between p-4 bg-white border-2 border-emerald-400 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 text-sm">{selectedPlayer.playerName}</p>
                        <p className="text-xs text-gray-600">{playerData?.team.name}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePlayer(slot)}
                        className="ml-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                        title="Remove player"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Available Players */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Available Players</h4>
                {availablePlayers.length === 0 ? (
                  <p className="text-sm text-gray-500 italic p-4 bg-white rounded-lg text-center border border-gray-200">
                    No available players
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1.5 pr-2 scrollbar-thin">
                    {availablePlayers.map((player) => {
                      // For multi-slot positions, prefer empty slots; otherwise replace first slot
                      const emptySlot = slots.find(slot => !selectedPlayers[slot]);
                      const targetSlot = emptySlot || slots[0];
                      const isAlreadySelected = Object.values(selectedPlayers).some(
                        p => p.playerId === player.id
                      );
                      const isTeamBlocked = isTeamUsed(player.teamId) && !isAlreadySelected;

                      return (
                        <button
                          key={player.id}
                          type="button"
                          onClick={() => selectPlayer(targetSlot, player)}
                          disabled={isAlreadySelected || isTeamBlocked}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                            isTeamBlocked
                              ? 'bg-red-50 border-2 border-red-200 text-gray-400 cursor-not-allowed opacity-70'
                              : isAlreadySelected
                              ? 'bg-emerald-50 border-2 border-emerald-400 text-gray-700 cursor-default'
                              : 'bg-white border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-gray-900 shadow-sm hover:shadow'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <p className={`font-semibold text-sm ${
                                isTeamBlocked ? 'text-gray-400' : 'text-gray-900'
                              }`}>
                                {player.name}
                              </p>
                              <p className={`text-xs mt-0.5 ${
                                isTeamBlocked ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {player.team.name}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-semibold px-2 py-1 rounded ${
                                isTeamBlocked 
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {player.position}
                              </span>
                              {isTeamBlocked && (
                                <span className="text-sm font-bold text-red-500" title="Team already used">
                                  ✕
                                </span>
                              )}
                              {isAlreadySelected && (
                                <span className="text-sm font-bold text-emerald-500" title="Already selected">
                                  ✓
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Link
          to={`/pools/${poolId}`}
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Pool
        </Link>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {isEditMode ? 'Edit Your Entry' : 'Create Your Entry'}
          </h1>
          <p className="text-gray-600 text-sm">{pool.name}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Entry Name */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <label htmlFor="entryName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Entry Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="entryName"
                  value={entryName}
                  onChange={(e) => setEntryName(e.target.value)}
                  placeholder="e.g., My Championship Team"
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  maxLength={50}
                  required
                  style={{ color: '#111827' }}
                />
              </div>

              {/* Position Selectors - Conditional Rendering */}
              {isOlympic ? renderOlympicPositions() : renderStandardPositions()}
            </div>

            {/* Sidebar - Summary */}
            <div className="space-y-4">
              {/* Progress */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Roster Progress</h3>

                {/* Olympic Hockey Country Distribution */}
                {isOlympic && (
                  <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-blue-900">
                        Country Distribution
                      </h4>
                      <span className="text-sm font-bold text-blue-700">
                        {usedCount}/{teams.length}
                      </span>
                    </div>
                    <div className="text-xs text-blue-800 mb-2">
                      One player per country required
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${(usedCount / teams.length) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Unused Teams/Countries */}
                <div className="mb-6">
                  <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">
                    {isOlympic ? 'Unused Countries' : 'Unused Teams'}
                  </h4>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto scrollbar-thin">
                    {isOlympic ? (
                      getUnusedCountries().map((country) => (
                        <div
                          key={country.id}
                          className="text-xs px-3 py-2 rounded-md bg-gray-50 text-gray-700 border border-gray-200"
                        >
                          {country.flag} {country.name}
                        </div>
                      ))
                    ) : (
                      teams.filter(team => !usedTeams.has(team.id)).map((team) => (
                        <div
                          key={team.id}
                          className="text-xs px-3 py-2 rounded-md bg-gray-50 text-gray-700 border border-gray-200"
                        >
                          {team.name}
                        </div>
                      ))
                    )}
                    {usedCount === teams.length && (
                      <p className="text-xs text-gray-500 italic text-center py-3 bg-gray-50 rounded-md">
                        {isOlympic ? 'All countries used' : 'All teams used'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Roster Summary */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">
                    Your Roster <span className="text-gray-400 font-normal ml-1">({totalSlots}/{requiredSlots})</span>
                  </h4>
                  <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-thin">
                    {isOlympic ? (
                      // Olympic roster summary with flags
                      Object.entries(OLYMPIC_POSITION_GROUPS).map(([key, group]) => {
                        const positionGroup = key as keyof typeof OLYMPIC_POSITION_GROUPS;
                        return (
                          <div key={positionGroup} className="text-sm">
                            <div className="font-semibold text-gray-700 mb-1.5 text-xs uppercase tracking-wide">
                              {group.label}
                            </div>
                            {group.slots.map((slot) => {
                              const selectedPlayer = selectedPlayers[slot];
                              const playerData = selectedPlayer 
                                ? players.find(p => p.id === selectedPlayer.playerId)
                                : null;
                              const countryFlag = playerData ? getCountryFlag(playerData.team.name) : '';
                              
                              return (
                                <div
                                  key={slot}
                                  className={`text-xs px-3 py-2 rounded-md mb-1.5 border ${
                                    selectedPlayer
                                      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                                      : 'bg-white text-gray-400 border-gray-200 border-dashed italic'
                                  }`}
                                >
                                  {selectedPlayer ? (
                                    <>
                                      <div className="font-semibold">{countryFlag} {selectedPlayer.playerName}</div>
                                      <div className="text-gray-600 mt-0.5">{playerData?.team.name}</div>
                                    </>
                                  ) : (
                                    <div className="text-center py-0.5">Empty</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })
                    ) : (
                      // Standard roster summary
                      sortPositionKeys(positions).map((position) => {
                        const positionConfig = positions[position as keyof typeof positions];
                        const slots = getPositionSlots(position);
                        
                        return (
                          <div key={position} className="text-sm">
                            <div className="font-semibold text-gray-700 mb-1.5 text-xs uppercase tracking-wide">
                              {positionConfig.label}
                            </div>
                            {slots.map((slot) => {
                              const selectedPlayer = selectedPlayers[slot];
                              const playerData = selectedPlayer 
                                ? players.find(p => p.id === selectedPlayer.playerId)
                                : null;
                              
                              return (
                                <div
                                  key={slot}
                                  className={`text-xs px-3 py-2 rounded-md mb-1.5 border ${
                                    selectedPlayer
                                      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                                      : 'bg-white text-gray-400 border-gray-200 border-dashed italic'
                                  }`}
                                >
                                  {selectedPlayer ? (
                                    <>
                                      <div className="font-semibold">{selectedPlayer.playerName}</div>
                                      <div className="text-gray-600 mt-0.5">{playerData?.team.name}</div>
                                    </>
                                  ) : (
                                    <div className="text-center py-0.5">Empty {slots.length > 1 ? `#${slot.slice(-1)}` : ''}</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Entry fee notice — payment happens after submit (Settle Up) */}
                {hasEntryFee && !isEditMode && (
                  <FeeNotice entryFee={Number(pool.entryFee)} />
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isRosterComplete() || submitting}
                  className={`w-full mt-6 py-3.5 px-4 rounded-lg font-bold transition-all text-sm ${
                    isRosterComplete() && !submitting
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {submitting
                    ? (isEditMode ? 'Updating...' : 'Submitting...')
                    : (isEditMode ? 'Update Entry' : 'Submit Entry')
                  }
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
